import React from 'react';
import i18n from 'i18next';
import PropTypes from 'prop-types';
import { css } from '@emotion/core';
import { system900, system800, system300 } from 'Mui/styles/color';
import Typography from '@material-ui/core/Typography';
import { replaceTextVariable } from 'utils/filter';

const Hero = ({ type, imageUrl, actionUri, variant }) => {
    return (
        <div css={heroStyle(type, imageUrl, variant)}>
            {Boolean(actionUri) ? (
                <a css={imageWrapperStyle} href={actionUri} alt={actionUri}>
                    <span></span>
                </a>
            ) : (
                <div css={imageWrapperStyle}>
                    <span></span>
                </div>
            )}
        </div>
    );
};

const Body = ({ type, isShowImage, children, variant }) => {
    return <div css={bodyStyle(type, isShowImage, variant)}>{children}</div>;
};

const Title = ({ children }) => {
    return <div css={titleStyle}>{children}</div>;
};

const Description = ({ children }) => {
    return <div css={descriptionStyle}>{children}</div>;
};

const Referral = ({ code }) => {
    return <div css={referralStyle}>{`${i18n.t('label.referral_code')}: ${code}`}</div>;
};

const ActionButton = ({ buttonConfig }) => {
    const dynamicProps = buttonConfig?.uri ? { href: buttonConfig.uri, alt: buttonConfig.uri } : {};
    return (
        <div css={actionStyle}>
            <a {...dynamicProps} css={buttonStyle(buttonConfig)}>
                <Typography
                    variant="subtitle2"
                    css={[
                        textStyle,
                        css`
                            color: ${buttonConfig.textColor};
                        `
                    ]}
                >
                    {buttonConfig.label}
                </Typography>
            </a>
        </div>
    );
};

const ReferralShareCard = ({
    variant = 'contained',
    type = 'default',
    title,
    description,
    imageUrl,
    isShowImage,
    buttonConfig = {},
    referralCode,
    isShowReferralCode
}) => {
    console.log('title', title);
    console.log('description', description);
    return (
        <div css={containerStyle(type)}>
            {isShowImage && <Hero type={type} imageUrl={imageUrl} variant={variant} />}
            <Body type={type} isShowImage={isShowImage} variant={variant}>
                {title && type === 'line' && (
                    <Title>{replaceTextVariable(title, { username: '' })}</Title>
                )}
                {description && type === 'line' && <Description>{description}</Description>}
                {description && type === 'default' && (
                    <Description>{replaceTextVariable(description, { username: '' })}</Description>
                )}
                {isShowReferralCode && type === 'line' && <Referral code={referralCode} />}
                {buttonConfig?.enableDisplay && type === 'line' && (
                    <ActionButton buttonConfig={buttonConfig} />
                )}
            </Body>
        </div>
    );
};

const containerStyle = type => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    color: ${system900.color};
    background: ${type === 'default' ? 'transparent' : '#fff'};
    border-radius: 12px;
    gap: ${type === 'default' && '8px'};
    width: 100%;
`;

const heroStyle = (type, imageUrl, variant) => css`
    width: 100%;
    border-top-right-radius: inherit;
    border-top-left-radius: inherit;
    overflow: hidden;
    ${
        type === 'default' &&
        `
        border-bottom-right-radius: inherit;
        border-bottom-left-radius: inherit;
    `
    }
    ${
        variant === 'outlined' &&
        `
    border: 1px solid ${system300.color};
`
    }
    ${
        type === 'line' &&
        `
        border-bottom-width: 0px;
    `
    }
    & span {
        display: block;
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        border-bottom-right-radius: inherit;
        border-bottom-left-radius: inherit;
       
        ${
            type === 'default' &&
            `
            border-bottom-right-radius: inherit;
            border-bottom-left-radius: inherit;
        `
        }
        background-image: url(${imageUrl});
        background-size: cover;
        background-repeat: no-repeat;
        background-position: center center;
        overflow: hidden;
`;

const imageWrapperStyle = css`
    display: block;
    position: relative;
    width: 100%;
    aspect-ratio: 1.52 / 1;
`;

const bodyStyle = (type, isShowImage, variant) => css`
    display: flex;
    flex-direction: column;
    padding-inline: 16px;
    padding-block: 12px;
    background: #fff;
    border-bottom-right-radius: inherit;
    border-bottom-left-radius: inherit;
    ${
        variant === 'outlined' &&
        `
        border: 1px solid ${system300.color};
    `
    }
    ${
        type === 'default' &&
        `
        border-top-right-radius: inherit;
        border-top-left-radius: inherit;
    `
    }

    ${
        type === 'line' &&
        !isShowImage &&
        `
        border-top-right-radius: inherit;
        border-top-left-radius: inherit;
    `
    }
    ${
        type === 'line' &&
        isShowImage &&
        `
        border-top-width: 0px;
    `
    }
    overflow: hidden;
    gap: 4px;
    width: 100%;
`;

const titleStyle = css`
    align-self: stretch;
    color: ${system900.color};
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: 0.15px;
    word-break: normal;
    word-wrap: break-word;
    text-overflow: clip;
`;

const descriptionStyle = css`
    align-self: stretch;
    color: ${system800.color};
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    letter-spacing: 0.1px;
    white-space: pre-line;
    word-break: normal;
    word-wrap: break-word;
    text-overflow: clip;
`;

const referralStyle = css`
    width: 100%;
    padding-block: 8px;
    margin-block: 4px;
    text-align: center;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    letter-spacing: 1.25px;
    text-transform: capitalize;
`;

const actionStyle = css`
    flex: none;
    flex-basis: auto;
    overflow: hidden;
    position: relative;
`;

const buttonStyle = buttonConfig => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 36px;
    padding: 0 16px;
    color: ${buttonConfig.textColor};
    background-color: ${buttonConfig.bgColor};
    border-radius: 8px;

    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    letter-spacing: 1.25px;
    text-transform: capitalize;
    text-decoration: none;
`;

const textStyle = css`
    overflow: hidden;
    text-overflow: ellipsis;
    box-sizing: border-box;
    white-space: nowrap;
`;

export default ReferralShareCard;

ReferralShareCard.propTypes = {
    type: PropTypes.oneOf(['default', 'line']),
    description: PropTypes.string,
    imageUrl: PropTypes.string,
    buttonConfig: PropTypes.shape({
        enableDisplay: PropTypes.bool,
        label: PropTypes.string,
        uri: PropTypes.string,
        textColor: PropTypes.string,
        bgColor: PropTypes.string
    }),
    referralCode: PropTypes.string,
    isShowReferralCode: PropTypes.bool
};
